import { ReactNode, useState, forwardRef, useImperativeHandle } from 'react';
import { FiEdit, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import {CustomButton} from '@/components/uiWrappers/button.tsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

interface EditableSectionProps {
  title: string;
  children: ReactNode;
  onEdit?: boolean;
  editModalTitle?: string;
  editModalContent?: ReactNode;
  editModalMaxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
  collapsible?: boolean;
  defaultExpanded?: boolean;
  customAction?: ReactNode;
}

export interface EditableSectionRef {
  closeModal: () => void;
}

export const EditableSection = forwardRef<EditableSectionRef, EditableSectionProps>(({
  title,
  children,
  onEdit = false,
  editModalTitle,
  editModalContent,
  editModalMaxWidth = 'xl',
  collapsible = false,
  defaultExpanded = false,
  customAction,
}, ref) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  useImperativeHandle(ref, () => ({
    closeModal: () => setIsEditModalOpen(false),
  }));

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {collapsible && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-gray-500 hover:text-brand transition-colors p-1 -ml-1"
              aria-label={isExpanded ? 'Collapse section' : 'Expand section'}
            >
              {isExpanded ? (
                <FiChevronUp className="w-5 h-5" />
              ) : (
                <FiChevronDown className="w-5 h-5" />
              )}
            </button>
          )}
          <h2 className="text-base font-sans font-bold text-brand uppercase">{title}</h2>
        </div>
        {onEdit && (
            <CustomButton variant={'secondary'}
            onClick={() => setIsEditModalOpen(true)}
            >
            <FiEdit className="w-5 h-5" />
              Edit
            </CustomButton>
          // <Button
          //   variant="text"
          //   leftIcon={<FiEdit />}
          //   onClick={() => setIsEditModalOpen(true)}
          //   className="gap-1"
          // >
          //   Edit
          // </Button>
        )}
        {customAction}
      </div>

      {/* Content */}
      {(!collapsible || isExpanded) && (
        <div className="animate-fadeIn">
          {children}
        </div>
      )}

      {/* Edit Modal */}
      {onEdit && (
        <Dialog open={isEditModalOpen} onOpenChange={(open) => !open && setIsEditModalOpen(false)}>
          <DialogContent
            className={`sm:max-w-${editModalMaxWidth} w-full max-h-[90vh] flex flex-col overflow-hidden`}
            showCloseButton={true}
          >
            <DialogHeader className="flex-shrink-0">
              <DialogTitle>{editModalTitle || `Edit ${title}`}</DialogTitle>
            </DialogHeader>
            <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
              {editModalContent || (
                <p className="text-gray-500">Edit form will be implemented here</p>
              )}
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
});

EditableSection.displayName = 'EditableSection';
